import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { UserIcon, ChevronDownIcon, PhoneIcon, MailIcon, FileTextIcon, XIcon, ExternalLinkIcon, TrendingUpIcon, CheckCircleIcon, SearchIcon, CheckIcon, UsersIcon } from 'lucide-react';
const CURRENT_MARKET_RATE = 6.25;
type ViewMode = 'eligible' | 'recaptured';
interface Borrower {
  id: string;
  name: string;
  loanAmount: number;
  rate: number;
  fundedDate: string;
  status: 'funded' | 'contacted';
  recaptured: boolean;
  recapturedAmount?: number;
  recapturedDate?: string;
  phone: string;
  email: string;
  propertyValue: number;
  loanTerm: number;
  loanType: 'Conventional' | 'FHA' | 'VA' | 'Jumbo';
}
interface LoanOfficer {
  id: string;
  name: string;
  borrowerIds: string[];
}
interface Team {
  id: string;
  teamLeader: string;
  loanOfficers: LoanOfficer[];
}
interface Division {
  id: string;
  name: string;
  divisionLeader: string;
  teams: Team[];
}
// ── Seeded PRNG (mulberry32) for deterministic data ──
function makeRng(seed: number) {
  let s = seed;
  return () => {
    s |= 0;
    s = s + 0x6d2b79f5 | 0;
    let t = Math.imul(s ^ s >>> 15, 1 | s);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function generateData() {
  const rng = makeRng(42);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rng() * arr.length)];
  const randInt = (min: number, max: number) => Math.floor(rng() * (max - min + 1)) + min;
  const randFloat = (min: number, max: number, decimals = 3) => parseFloat((rng() * (max - min) + min).toFixed(decimals));
  const firstNames = ['James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda', 'William', 'Barbara', 'David', 'Elizabeth', 'Richard', 'Susan', 'Joseph', 'Jessica', 'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Lisa', 'Daniel', 'Nancy', 'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra', 'Donald', 'Ashley', 'Steven', 'Dorothy', 'Paul', 'Kimberly', 'Andrew', 'Emily', 'Joshua', 'Donna', 'Kenneth', 'Michelle', 'Kevin', 'Carol', 'Brian', 'Amanda', 'George', 'Melissa', 'Timothy', 'Deborah', 'Ronald', 'Stephanie', 'Edward', 'Rebecca', 'Jason', 'Sharon', 'Jeffrey', 'Laura', 'Ryan', 'Cynthia', 'Jacob', 'Kathleen', 'Gary', 'Amy', 'Nicholas', 'Angela', 'Eric', 'Shirley', 'Jonathan', 'Anna', 'Stephen', 'Brenda', 'Larry', 'Pamela', 'Justin', 'Emma', 'Scott', 'Nicole', 'Brandon', 'Helen', 'Raymond', 'Samantha', 'Frank', 'Katherine', 'Gregory', 'Christine', 'Benjamin', 'Debra', 'Samuel', 'Rachel', 'Patrick', 'Carolyn', 'Alexander', 'Janet', 'Jack', 'Catherine', 'Dennis', 'Maria', 'Jerry', 'Heather', 'Tyler', 'Diane', 'Aaron', 'Julie'];
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young', 'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores', 'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell', 'Carter', 'Roberts', 'Turner', 'Phillips', 'Evans', 'Parker', 'Collins', 'Edwards', 'Stewart', 'Flores', 'Morris', 'Nguyen', 'Murphy', 'Rivera', 'Cook', 'Rogers', 'Morgan', 'Peterson', 'Cooper', 'Reed', 'Bailey', 'Bell', 'Gomez', 'Kelly', 'Howard', 'Ward', 'Cox', 'Diaz', 'Richardson', 'Wood', 'Watson', 'Brooks', 'Bennett', 'Gray', 'James', 'Reyes', 'Cruz', 'Hughes', 'Price', 'Myers', 'Long', 'Foster', 'Sanders', 'Ross', 'Morales', 'Powell', 'Sullivan', 'Russell', 'Ortiz', 'Jenkins', 'Gutierrez', 'Perry', 'Butler', 'Barnes', 'Fisher', 'Henderson'];
  const loanTypes: Borrower['loanType'][] = ['Conventional', 'Conventional', 'Conventional', 'FHA', 'FHA', 'VA', 'VA', 'Jumbo'];
  const statuses: Borrower['status'][] = ['funded', 'funded', 'funded', 'contacted'];
  // ── Generate 2000 borrowers ──
  const borrowers: Borrower[] = [];
  const usedNames = new Set<string>();
  for (let i = 1; i <= 2000; i++) {
    let name: string;
    do {
      name = `${pick(firstNames)} ${pick(lastNames)}`;
    } while (usedNames.has(name));
    usedNames.add(name);
    const loanType = pick(loanTypes);
    const isJumbo = loanType === 'Jumbo';
    const loanAmount = isJumbo ? randInt(650000, 1400000) : loanType === 'FHA' ? randInt(120000, 420000) : loanType === 'VA' ? randInt(150000, 550000) : randInt(200000, 750000);
    const ltv = randFloat(0.65, 0.92, 2);
    const propertyValue = Math.round(loanAmount / ltv / 1000) * 1000;
    // Rates: spread across all three filter buckets
    const rateBucket = rng();
    let rate: number;
    if (rateBucket < 0.33) rate = randFloat(5.875, 6.499); // below 6.5%
    else if (rateBucket < 0.66) rate = randFloat(6.5, 7.0); // 6.5–7%
    else rate = randFloat(7.001, 8.25); // above 7%
    // Funded dates spread across last ~3 years
    const daysAgo = randInt(30, 1100);
    const fundedDate = new Date(Date.now() - daysAgo * 86400000);
    const fundedStr = fundedDate.toISOString().split('T')[0];
    const recaptured = rng() < 0.32;
    const recapturedAmount = recaptured ? Math.round(loanAmount * randFloat(0.94, 0.99, 2)) : undefined;
    const recapturedDate = recaptured ? new Date(fundedDate.getTime() + randInt(180, 540) * 86400000).toISOString().split('T')[0] : undefined;
    const areaCode = randInt(200, 989);
    const phone = `(${areaCode}) ${randInt(200, 999)}-${randInt(1000, 9999)}`;
    const emailFirst = name.split(' ')[0].toLowerCase();
    const emailLast = name.split(' ')[1].toLowerCase();
    const email = `${emailFirst}.${emailLast[0]}${i}@example.com`;
    borrowers.push({
      id: String(i),
      name,
      loanAmount,
      rate,
      fundedDate: fundedStr,
      status: pick(statuses),
      recaptured,
      recapturedAmount,
      recapturedDate,
      phone,
      email,
      propertyValue,
      loanTerm: 30,
      loanType
    });
  }
  // ── LO name pool ──
  const loNames = [
  // West Div – Team 1
  'Abdul Baksh', 'Alvin Jabboury', 'Riad Ahmed', 'Sandra Hanna', 'Selena Touma', 'Carlos Vega', 'Priya Nair', 'Marcus Webb',
  // West Div – Team 2
  'Maria Santos', 'Tom Richards', 'Kevin Patel', 'Diane Chu', 'Omar Hassan', 'Brittany Fox', 'Derek Stone',
  // West Div – Team 3
  'Fiona Grant', 'Leo Castillo', 'Yuki Tanaka', 'Brendan Walsh', 'Aisha Okafor', 'Trevor Mills', 'Camille Dupont',
  // East Div – Team 1
  'James Liu', 'Diana Reyes', 'Brian Foster', 'Tanya Osei', 'Nathan Cross', 'Ingrid Holm', 'Patrick Dunn',
  // East Div – Team 2
  'Nicole Adams', 'Ryan Cooper', 'Megan Brooks', 'Sanjay Mehta', 'Layla Hassan', 'Garrett Flynn', 'Vivian Tran',
  // East Div – Team 3
  'Harold Kim', 'Serena Watts', 'Darius Cole', 'Monica Ferreira', 'Elliot Shaw', 'Jasmine Patel', 'Connor Reid'];
  // Distribute 2000 borrowers evenly across 42 LOs
  const loCount = loNames.length;
  const loAssignments: string[][] = Array.from({
    length: loCount
  }, () => []);
  borrowers.forEach((b, idx) => {
    loAssignments[idx % loCount].push(b.id);
  });
  // ── Build DIVISIONS ──
  const makeLOs = (startIdx: number, count: number): LoanOfficer[] => Array.from({
    length: count
  }, (_, i) => ({
    id: `lo-${startIdx + i + 1}`,
    name: loNames[startIdx + i],
    borrowerIds: loAssignments[startIdx + i]
  }));
  const divisions: Division[] = [{
    id: 'div-1',
    name: 'West Division',
    divisionLeader: 'Alvin Jabboury',
    teams: [{
      id: 'team-1',
      teamLeader: 'Alvin Jabboury',
      loanOfficers: makeLOs(0, 8)
    }, {
      id: 'team-2',
      teamLeader: 'Maria Santos',
      loanOfficers: makeLOs(8, 7)
    }, {
      id: 'team-3',
      teamLeader: 'Fiona Grant',
      loanOfficers: makeLOs(15, 7)
    }]
  }, {
    id: 'div-2',
    name: 'East Division',
    divisionLeader: 'Patricia Coleman',
    teams: [{
      id: 'team-4',
      teamLeader: 'James Liu',
      loanOfficers: makeLOs(22, 7)
    }, {
      id: 'team-5',
      teamLeader: 'Nicole Adams',
      loanOfficers: makeLOs(29, 7)
    }, {
      id: 'team-6',
      teamLeader: 'Harold Kim',
      loanOfficers: makeLOs(36, 6)
    }]
  }];
  return {
    borrowers,
    divisions
  };
}
const {
  borrowers: INITIAL_BORROWERS,
  divisions: DIVISIONS
} = generateData();
const borrowerMap = new Map(INITIAL_BORROWERS.map(b => [b.id, b]));
function calcMonthlyPayment(principal: number, annualRate: number, years: number) {
  const r = annualRate / 100 / 12;
  const n = years * 12;
  return principal * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
}
function formatUSD(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
}
function formatCompactUSD(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(amount);
}
function generateCallScript(borrower: Borrower): {
  section: string;
  text: string;
}[] {
  const firstName = borrower.name.split(' ')[0];
  const rateDiff = borrower.rate - CURRENT_MARKET_RATE;
  const monthsSinceFunding = Math.max(1, Math.round((Date.now() - new Date(borrower.fundedDate).getTime()) / (1000 * 60 * 60 * 24 * 30)));
  const currentPayment = calcMonthlyPayment(borrower.loanAmount, borrower.rate, borrower.loanTerm);
  const newPayment = calcMonthlyPayment(borrower.loanAmount, CURRENT_MARKET_RATE, borrower.loanTerm);
  const monthlySavings = currentPayment - newPayment;
  const yearlySavings = monthlySavings * 12;
  const estimatedEquity = borrower.propertyValue - borrower.loanAmount;
  const appreciationEstimate = borrower.propertyValue * 0.04 * (monthsSinceFunding / 12);
  const totalEquity = estimatedEquity + appreciationEstimate;
  const script: {
    section: string;
    text: string;
  }[] = [];
  script.push({
    section: 'Opening',
    text: `Hi ${firstName}, this is John — I was your loan officer when we closed on your home back in ${new Date(borrower.fundedDate).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    })}. How have you been? I hope you're enjoying the home!`
  });
  script.push({
    section: 'Reason for Call',
    text: `The reason I'm reaching out is that I like to keep an eye on the market for my past clients, and I noticed some changes that could really benefit you.`
  });
  if (rateDiff > 0) {
    script.push({
      section: 'Rate Opportunity',
      text: `When we closed your loan, your rate was ${borrower.rate.toFixed(3)}%. Right now, rates have come down to around ${CURRENT_MARKET_RATE.toFixed(2)}% — that's a ${rateDiff.toFixed(3)}% reduction. On your ${formatUSD(borrower.loanAmount)} loan, that could save you about ${formatUSD(Math.round(monthlySavings))} per month, which adds up to ${formatUSD(Math.round(yearlySavings))} a year.`
    });
  } else {
    script.push({
      section: 'Cash-Out Opportunity',
      text: `Even though rates are similar to when we closed, there's actually a great opportunity here. With the equity you've built up in your home, a cash-out refinance could give you access to funds for renovations or paying off higher-interest debt.`
    });
  }
  if (rateDiff > 0.25) {
    const lifetimeSavings = (currentPayment - newPayment) * borrower.loanTerm * 12;
    script.push({
      section: 'Long-Term Impact',
      text: `And if we look at the bigger picture over the life of your ${borrower.loanTerm}-year loan, that rate difference could save you around ${formatUSD(Math.round(lifetimeSavings))} total.`
    });
  }
  script.push({
    section: 'Equity Position',
    text: `Another thing worth mentioning — it's been about ${monthsSinceFunding} months since we closed, and between your payments and property values, you're sitting on roughly ${formatUSD(Math.round(totalEquity))} in equity.`
  });
  script.push({
    section: 'Close',
    text: `I'd love to run the exact numbers for you — it only takes a few minutes and there's no obligation. Would you have some time this week for a quick 10-minute call?`
  });
  script.push({
    section: 'If They Hesitate',
    text: `I completely understand — it's a big decision. How about I just send you a quick email with the numbers so you can look at it on your own time?`
  });
  return script;
}
function getLOStats(lo: LoanOfficer, mode: ViewMode) {
  const allBorrowers = lo.borrowerIds.map(id => borrowerMap.get(id)).filter(Boolean) as Borrower[];
  const borrowers = allBorrowers.filter(b => mode === 'eligible' ? !b.recaptured : b.recaptured);
  const volume = mode === 'eligible' ? borrowers.reduce((sum, b) => sum + b.loanAmount, 0) : borrowers.reduce((sum, b) => sum + (b.recapturedAmount ?? b.loanAmount), 0);
  return {
    count: borrowers.length,
    volume,
    borrowers
  };
}
function getTeamStats(team: Team, mode: ViewMode) {
  let count = 0,
    volume = 0;
  for (const lo of team.loanOfficers) {
    const s = getLOStats(lo, mode);
    count += s.count;
    volume += s.volume;
  }
  return {
    count,
    volume
  };
}
function getDivisionStats(division: Division, mode: ViewMode) {
  let count = 0,
    volume = 0;
  for (const team of division.teams) {
    const s = getTeamStats(team, mode);
    count += s.count;
    volume += s.volume;
  }
  return {
    count,
    volume
  };
}
/* ── Shared row-stat renderer for consistent alignment ── */
function RowStats({
  count,
  volume,
  mode
}: {
  count: number;
  volume: number;
  mode: ViewMode;
}) {
  const isEligible = mode === 'eligible';
  return <div className="flex items-center gap-6 flex-shrink-0" data-id="element-1">
      <span className="w-[6.5rem] text-right" data-id="element-2">
        {count > 0 ? <Badge variant={isEligible ? 'blue' : 'success'} data-id="element-3">
            {count} {isEligible ? 'eligible' : 'recaptured'}
          </Badge> : <span className="text-xs text-gray-400" data-id="element-4">0</span>}
      </span>
      <span className={`w-24 text-right text-sm font-medium tabular-nums ${count > 0 ? isEligible ? 'text-gray-900' : 'text-green-700' : 'text-gray-400'}`} data-id="element-5">
        {count > 0 ? formatCompactUSD(volume) : '—'}
      </span>
    </div>;
}
interface FilterOption {
  value: string;
  label: string;
}
function MultiSelectFilter({
  label,
  options,
  selected,
  onChange
}: {
  label: string;
  options: FilterOption[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);
  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onChange(next);
  };
  const activeCount = selected.size;
  const displayLabel = activeCount === 0 ? label : `${label} (${activeCount})`;
  return <div className="relative" ref={ref} data-id="element-6">
      <button onClick={() => setOpen(!open)} className={`h-9 pl-3 pr-2.5 rounded-lg border text-sm flex items-center gap-1.5 cursor-pointer transition-all whitespace-nowrap ${activeCount > 0 ? 'border-blue-300 bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'}`} data-id="element-7">
        {displayLabel}
        <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-8" />
      </button>

      {open && <div className="absolute top-full left-0 mt-1.5 bg-white rounded-lg border border-gray-200 shadow-lg z-30 min-w-[180px] py-1.5 animate-in fade-in slide-in-from-top-1" data-id="element-9">
          {options.map(option => {
        const isSelected = selected.has(option.value);
        return <button key={option.value} onClick={() => toggle(option.value)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${isSelected ? 'text-blue-700 bg-blue-50/50' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-10">
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`} data-id="element-11">
                  {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-12" />}
                </span>
                {option.label}
              </button>;
      })}
          {activeCount > 0 && <>
              <div className="border-t border-gray-100 my-1" data-id="element-13"></div>
              <button onClick={() => onChange(new Set())} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-14">
                <XIcon className="w-3.5 h-3.5" data-id="element-15" />
                Clear selection
              </button>
            </>}
        </div>}
    </div>;
}
const RATE_OPTIONS: FilterOption[] = [{
  value: 'above-7',
  label: 'Above 7%'
}, {
  value: '6.5-7',
  label: '6.5% – 7%'
}, {
  value: 'below-6.5',
  label: 'Below 6.5%'
}];
const AMOUNT_OPTIONS: FilterOption[] = [{
  value: 'under-300k',
  label: 'Under $300K'
}, {
  value: '300k-500k',
  label: '$300K – $500K'
}, {
  value: 'over-500k',
  label: 'Over $500K'
}];
const DATE_OPTIONS: FilterOption[] = [{
  value: 'last-3',
  label: 'Last 3 months'
}, {
  value: '3-6',
  label: '3–6 months ago'
}, {
  value: 'over-6',
  label: '6+ months ago'
}];
const TYPE_OPTIONS: FilterOption[] = [{
  value: 'Conventional',
  label: 'Conventional'
}, {
  value: 'FHA',
  label: 'FHA'
}, {
  value: 'VA',
  label: 'VA'
}, {
  value: 'Jumbo',
  label: 'Jumbo'
}];
const DIVISION_OPTIONS: FilterOption[] = [{
  value: 'div-1',
  label: 'West Division'
}, {
  value: 'div-2',
  label: 'East Division'
}];
export function BorrowerList() {
  const [viewMode, setViewMode] = useState<ViewMode>('eligible');
  const [expandedDivisions, setExpandedDivisions] = useState<Set<string>>(new Set(['div-1']));
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set(['team-1']));
  const [expandedLOs, setExpandedLOs] = useState<Set<string>>(new Set());
  const [scriptBorrowerId, setScriptBorrowerId] = useState<string | null>(null);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [rateFilters, setRateFilters] = useState<Set<string>>(new Set());
  const [amountFilters, setAmountFilters] = useState<Set<string>>(new Set());
  const [dateFilters, setDateFilters] = useState<Set<string>>(new Set());
  const [loanTypeFilters, setLoanTypeFilters] = useState<Set<string>>(new Set());
  const [divisionFilters, setDivisionFilters] = useState<Set<string>>(new Set());
  const toggle = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
    setter(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearFilters = () => {
    setSearchQuery('');
    setRateFilters(new Set());
    setAmountFilters(new Set());
    setDateFilters(new Set());
    setLoanTypeFilters(new Set());
    setDivisionFilters(new Set());
  };
  const hasActiveFilters = searchQuery || rateFilters.size > 0 || amountFilters.size > 0 || dateFilters.size > 0 || loanTypeFilters.size > 0 || divisionFilters.size > 0;
  const matchesFilters = (borrower: Borrower) => {
    if (rateFilters.size > 0) {
      const matchesRate = Array.from(rateFilters).some(filter => {
        if (filter === 'above-7') return borrower.rate > 7;
        if (filter === '6.5-7') return borrower.rate >= 6.5 && borrower.rate <= 7;
        if (filter === 'below-6.5') return borrower.rate < 6.5;
        return false;
      });
      if (!matchesRate) return false;
    }
    if (amountFilters.size > 0) {
      const matchesAmount = Array.from(amountFilters).some(filter => {
        if (filter === 'under-300k') return borrower.loanAmount < 300000;
        if (filter === '300k-500k') return borrower.loanAmount >= 300000 && borrower.loanAmount <= 500000;
        if (filter === 'over-500k') return borrower.loanAmount > 500000;
        return false;
      });
      if (!matchesAmount) return false;
    }
    if (dateFilters.size > 0) {
      const fundedDate = new Date(borrower.fundedDate);
      const now = new Date();
      const monthsDiff = (now.getFullYear() - fundedDate.getFullYear()) * 12 + (now.getMonth() - fundedDate.getMonth());
      const matchesDate = Array.from(dateFilters).some(filter => {
        if (filter === 'last-3') return monthsDiff <= 3;
        if (filter === '3-6') return monthsDiff > 3 && monthsDiff <= 6;
        if (filter === 'over-6') return monthsDiff > 6;
        return false;
      });
      if (!matchesDate) return false;
    }
    if (loanTypeFilters.size > 0) {
      if (!loanTypeFilters.has(borrower.loanType)) return false;
    }
    return true;
  };
  const filteredDivisions = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return DIVISIONS.filter(div => divisionFilters.size === 0 || divisionFilters.has(div.id)).map(div => {
      return {
        ...div,
        teams: div.teams.map(team => {
          return {
            ...team,
            loanOfficers: team.loanOfficers.map(lo => {
              const filteredBorrowerIds = lo.borrowerIds.filter(id => {
                const b = borrowerMap.get(id);
                if (!b) return false;
                if (viewMode === 'eligible' && b.recaptured) return false;
                if (viewMode === 'recaptured' && !b.recaptured) return false;
                if (!matchesFilters(b)) return false;
                return true;
              });
              const searchMatchLO = q && lo.name.toLowerCase().includes(q);
              const searchMatchTeam = q && team.teamLeader.toLowerCase().includes(q);
              const searchMatchDiv = q && div.divisionLeader.toLowerCase().includes(q);
              const finalBorrowerIds = filteredBorrowerIds.filter(id => {
                if (!q) return true;
                if (searchMatchLO || searchMatchTeam || searchMatchDiv) return true;
                const b = borrowerMap.get(id);
                return b && b.name.toLowerCase().includes(q);
              });
              return {
                ...lo,
                borrowerIds: finalBorrowerIds
              };
            }).filter(lo => lo.borrowerIds.length > 0 || q && lo.name.toLowerCase().includes(q))
          };
        }).filter(team => team.loanOfficers.length > 0 || q && team.teamLeader.toLowerCase().includes(q))
      };
    }).filter(div => div.teams.length > 0 || q && div.divisionLeader.toLowerCase().includes(q));
  }, [searchQuery, rateFilters, amountFilters, dateFilters, loanTypeFilters, divisionFilters, viewMode]);
  // Calculate overall stats based on filters (ignoring viewMode for the totals)
  const filteredTotalStats = useMemo(() => {
    const allFilteredBorrowers = INITIAL_BORROWERS.filter(b => matchesFilters(b));
    // Apply division filter
    const activeDivisionIds = divisionFilters.size > 0 ? Array.from(divisionFilters) : DIVISIONS.map(d => d.id);
    const validBorrowerIds = new Set<string>();
    DIVISIONS.filter(d => activeDivisionIds.includes(d.id)).forEach(div => {
      div.teams.forEach(team => {
        team.loanOfficers.forEach(lo => {
          lo.borrowerIds.forEach(id => validBorrowerIds.add(id));
        });
      });
    });
    const finalBorrowers = allFilteredBorrowers.filter(b => validBorrowerIds.has(b.id));
    const eligible = finalBorrowers.filter(b => !b.recaptured);
    const recaptured = finalBorrowers.filter(b => b.recaptured);
    const recapturedVolume = recaptured.reduce((sum, b) => sum + (b.recapturedAmount ?? b.loanAmount), 0);
    return {
      eligibleCount: eligible.length,
      recapturedCount: recaptured.length,
      recapturedVolume,
      totalCount: finalBorrowers.length,
      recaptureRate: finalBorrowers.length > 0 ? recaptured.length / finalBorrowers.length * 100 : 0
    };
  }, [rateFilters, amountFilters, dateFilters, loanTypeFilters, divisionFilters]);
  // Calculate organization stats from the current filtered tree
  const orgStats = useMemo(() => {
    let teamsCount = 0;
    let losCount = 0;
    let borrowersCount = 0;
    filteredDivisions.forEach(div => {
      teamsCount += div.teams.length;
      div.teams.forEach(team => {
        losCount += team.loanOfficers.length;
        team.loanOfficers.forEach(lo => {
          borrowersCount += lo.borrowerIds.length;
        });
      });
    });
    return {
      divisions: filteredDivisions.length,
      teams: teamsCount,
      los: losCount,
      borrowers: borrowersCount
    };
  }, [filteredDivisions]);
  const isEligible = viewMode === 'eligible';
  const scriptBorrower = scriptBorrowerId ? borrowerMap.get(scriptBorrowerId) ?? null : null;
  const callScript = scriptBorrower ? generateCallScript(scriptBorrower) : [];
  return <Card className="flex flex-col overflow-hidden" data-id="element-16">
      {/* ── Toolbar: Title + Toggle + Search + Filters ── */}
      <div className="px-6 pt-5 pb-4 space-y-4 bg-white" data-id="element-17">
        {/* Row 1: Title & Toggle */}
        <div className="flex items-start sm:items-center justify-between gap-4 flex-wrap" data-id="element-18">
          <div data-id="element-19">
            <h2 className="text-lg font-semibold text-gray-900 leading-tight" data-id="element-20">
              Division Performance
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-21">
              {isEligible ? 'Eligible refinance borrowers by division, team & officer' : 'Recaptured borrowers by division, team & officer'}
            </p>
          </div>

          {/* Segmented Toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5" data-id="element-22">
            <button onClick={() => setViewMode('eligible')} className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${isEligible ? 'bg-white text-blue-700 shadow-sm ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-23">
              <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold leading-none ${isEligible ? 'bg-blue-100 text-blue-700' : 'bg-gray-200/80 text-gray-500'}`} data-id="element-24">
                {filteredTotalStats.eligibleCount}
              </span>
              Eligible
            </button>
            <button onClick={() => setViewMode('recaptured')} className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${!isEligible ? 'bg-white text-green-700 shadow-sm ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-25">
              <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold leading-none ${!isEligible ? 'bg-green-100 text-green-700' : 'bg-gray-200/80 text-gray-500'}`} data-id="element-26">
                {filteredTotalStats.recapturedCount}
              </span>
              Recaptured
            </button>
          </div>
        </div>

        {/* Row 2: Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center" data-id="element-27">
          <div className="relative w-full md:w-64 flex-shrink-0" data-id="element-28">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-29" />
            <input type="text" placeholder="Search by name…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-9 pl-9 pr-4 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" data-id="element-30" />
          </div>

          <div className="hidden md:block w-px h-6 bg-gray-200 flex-shrink-0" data-id="element-31"></div>

          <div className="flex flex-wrap gap-2 w-full md:w-auto items-center" data-id="element-32">
            <MultiSelectFilter label="Division" options={DIVISION_OPTIONS} selected={divisionFilters} onChange={setDivisionFilters} data-id="element-33" />
            <MultiSelectFilter label="Rate" options={RATE_OPTIONS} selected={rateFilters} onChange={setRateFilters} data-id="element-34" />
            <MultiSelectFilter label="Amount" options={AMOUNT_OPTIONS} selected={amountFilters} onChange={setAmountFilters} data-id="element-35" />
            <MultiSelectFilter label="Funded" options={DATE_OPTIONS} selected={dateFilters} onChange={setDateFilters} data-id="element-36" />
            <MultiSelectFilter label="Type" options={TYPE_OPTIONS} selected={loanTypeFilters} onChange={setLoanTypeFilters} data-id="element-37" />

            {hasActiveFilters && <button onClick={clearFilters} className="h-9 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5 px-3 rounded-lg border border-gray-200 hover:bg-gray-50 bg-white transition-colors" data-id="element-38">
                <XIcon className="w-3.5 h-3.5" data-id="element-39" />
                Clear all
              </button>}
          </div>
        </div>
      </div>

      {/* ── Summary Tiles ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 px-6 pb-5 bg-white" data-id="element-40">
        {/* Card 1: Organization Summary */}
        <Card className="shadow-sm border-gray-200" data-id="element-41">
          <CardContent className="p-4 h-full flex flex-col" data-id="element-42">
            <div className="flex items-center gap-2 mb-3" data-id="element-43">
              <div className="bg-blue-100 p-1.5 rounded-lg" data-id="element-44">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-45" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm" data-id="element-46">
                Organization
              </h3>
            </div>
            <div className="space-y-0 flex-1" data-id="element-47">
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100" data-id="element-48">
                <span className="text-gray-500" data-id="element-49">Divisions</span>
                <span className="font-semibold text-gray-900 tabular-nums" data-id="element-50">
                  {orgStats.divisions}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100" data-id="element-51">
                <span className="text-gray-500" data-id="element-52">Teams</span>
                <span className="font-semibold text-gray-900 tabular-nums" data-id="element-53">
                  {orgStats.teams}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100" data-id="element-54">
                <span className="text-gray-500" data-id="element-55">Loan Officers</span>
                <span className="font-semibold text-gray-900 tabular-nums" data-id="element-56">
                  {orgStats.los}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm py-1.5" data-id="element-57">
                <span className="text-gray-500" data-id="element-58">Total Borrowers</span>
                <span className="font-semibold text-blue-600 tabular-nums" data-id="element-59">
                  {orgStats.borrowers}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Recaptured Summary */}
        <Card className="shadow-sm border-gray-200" data-id="element-60">
          <CardContent className="p-4 h-full flex flex-col" data-id="element-61">
            <div className="flex items-center gap-2 mb-3" data-id="element-62">
              <div className="bg-green-100 p-1.5 rounded-lg" data-id="element-63">
                <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-64" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm" data-id="element-65">
                Recaptured
              </h3>
            </div>
            <div className="space-y-0 flex-1" data-id="element-66">
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100" data-id="element-67">
                <span className="text-gray-500" data-id="element-68">Borrowers</span>
                <span className="font-semibold text-green-700 tabular-nums" data-id="element-69">
                  {filteredTotalStats.recapturedCount}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100" data-id="element-70">
                <span className="text-gray-500" data-id="element-71">Volume</span>
                <span className="font-semibold text-green-700 tabular-nums" data-id="element-72">
                  {formatCompactUSD(filteredTotalStats.recapturedVolume)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100" data-id="element-73">
                <span className="text-gray-500" data-id="element-74">Recapture Rate</span>
                <span className="font-semibold text-green-700 tabular-nums" data-id="element-75">
                  {filteredTotalStats.recaptureRate.toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center justify-between text-sm py-1.5" data-id="element-76">
                <span className="text-gray-500" data-id="element-77">Still Eligible</span>
                <span className="font-semibold text-blue-600 tabular-nums" data-id="element-78">
                  {filteredTotalStats.eligibleCount}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Volume Goal (Static) */}
        <Card className="shadow-sm border-gray-200" data-id="element-79">
          <CardContent className="p-4 h-full flex flex-col" data-id="element-80">
            <div className="flex items-center justify-between mb-3" data-id="element-81">
              <div className="flex items-center gap-2" data-id="element-82">
                <div className="bg-emerald-100 p-1.5 rounded-lg" data-id="element-83">
                  <TrendingUpIcon className="w-4 h-4 text-emerald-600" data-id="element-84" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm" data-id="element-85">
                  Monthly Goal
                </h3>
              </div>
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-86">
                4 days left
              </span>
            </div>
            <div className="flex-1 flex flex-col justify-end" data-id="element-87">
              <div className="flex justify-between text-sm mb-1.5" data-id="element-88">
                <span className="text-gray-500" data-id="element-89">Refinance Volume</span>
                <span className="font-semibold text-gray-900 tabular-nums" data-id="element-90">
                  75%
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-400 mb-3" data-id="element-91">
                <span data-id="element-92">$3.75M of $5M</span>
                <span data-id="element-93">$1.25M remaining</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-94">
                <div className="bg-emerald-500 h-full rounded-full transition-all duration-1000 ease-out" style={{
                width: '75%'
              }} data-id="element-95" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Outreach Goal (Static) */}
        <Card className="shadow-sm border-gray-200" data-id="element-96">
          <CardContent className="p-4 h-full flex flex-col" data-id="element-97">
            <div className="flex items-center justify-between mb-3" data-id="element-98">
              <div className="flex items-center gap-2" data-id="element-99">
                <div className="bg-purple-100 p-1.5 rounded-lg" data-id="element-100">
                  <PhoneIcon className="w-4 h-4 text-purple-600" data-id="element-101" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm" data-id="element-102">
                  Outreach
                </h3>
              </div>
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-103">
                Daily
              </span>
            </div>
            <div className="flex-1 flex flex-col justify-end" data-id="element-104">
              <div className="flex justify-between text-sm mb-1.5" data-id="element-105">
                <span className="text-gray-500" data-id="element-106">Calls Made</span>
                <span className="font-semibold text-gray-900 tabular-nums" data-id="element-107">
                  45 / 60
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-400 mb-3" data-id="element-108">
                <span data-id="element-109">15 remaining</span>
                <span className="text-green-600 font-medium" data-id="element-110">
                  +12 vs yesterday
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-111">
                <div className="bg-purple-500 h-full rounded-full transition-all duration-1000 ease-out" style={{
                width: '75%'
              }} data-id="element-112" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Column Header ── */}
      <div className="bg-gray-50 border-y border-gray-200 px-6 py-2.5" data-id="element-113">
        <div className="flex items-center justify-between" data-id="element-114">
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-115">
            Division / Team / Officer
          </span>
          <div className="flex items-center gap-6" data-id="element-116">
            <span className="w-[6.5rem] text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-117">
              {isEligible ? 'Eligible' : 'Recaptured'}
            </span>
            <span className="w-24 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-118">
              Volume
            </span>
          </div>
        </div>
      </div>

      {/* ── Accordion ── */}
      <CardContent className="p-0 flex-1 overflow-y-auto" data-id="element-119">
        {filteredDivisions.length === 0 ? <div className="py-20 flex flex-col items-center justify-center text-center px-8" data-id="element-120">
            <div className="bg-gray-100 p-3.5 rounded-full mb-3" data-id="element-121">
              <SearchIcon className="w-6 h-6 text-gray-400" data-id="element-122" />
            </div>
            <p className="text-sm font-medium text-gray-900" data-id="element-123">
              No results found
            </p>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-124">
              Try adjusting your filters or search term.
            </p>
          </div> : filteredDivisions.map(division => {
        const divStats = getDivisionStats(division, viewMode);
        const isOpen = expandedDivisions.has(division.id);
        return <div key={division.id} className="border-b border-gray-100 last:border-b-0" data-id="element-125">
                {/* Division Row */}
                <button onClick={() => toggle(setExpandedDivisions, division.id)} className="w-full flex items-center justify-between px-6 py-3 hover:bg-gray-50/80 transition-colors text-left group" data-id="element-126">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-127">
                    <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? '' : '-rotate-90'}`} data-id="element-128" />
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-129">
                      <UserIcon className="w-4 h-4 text-gray-500" data-id="element-130" />
                    </div>
                    <div className="min-w-0" data-id="element-131">
                      <span className="font-semibold text-gray-900" data-id="element-132">
                        {division.divisionLeader}
                      </span>
                      <span className="text-sm text-gray-400 ml-1.5" data-id="element-133">
                        {division.name}
                      </span>
                    </div>
                  </div>
                  <RowStats count={divStats.count} volume={divStats.volume} mode={viewMode} data-id="element-134" />
                </button>

                {/* Teams */}
                {isOpen && division.teams.map(team => {
            const teamStats = getTeamStats(team, viewMode);
            const teamOpen = expandedTeams.has(team.id);
            return <div key={team.id} data-id="element-135">
                        <button onClick={() => toggle(setExpandedTeams, team.id)} className="w-full flex items-center justify-between pl-12 pr-6 py-2.5 hover:bg-gray-50/80 transition-colors text-left border-t border-gray-50" data-id="element-136">
                          <div className="flex items-center gap-3 min-w-0" data-id="element-137">
                            <ChevronDownIcon className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${teamOpen ? '' : '-rotate-90'}`} data-id="element-138" />
                            <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-139">
                              <UserIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-140" />
                            </div>
                            <span className="font-medium text-gray-800" data-id="element-141">
                              {team.teamLeader}
                            </span>
                            <span className="text-[11px] text-gray-400 uppercase tracking-wide" data-id="element-142">
                              Team Lead
                            </span>
                          </div>
                          <RowStats count={teamStats.count} volume={teamStats.volume} mode={viewMode} data-id="element-143" />
                        </button>

                        {/* Loan Officers */}
                        {teamOpen && team.loanOfficers.map(lo => {
                const loStats = getLOStats(lo, viewMode);
                const loOpen = expandedLOs.has(lo.id);
                return <div key={lo.id} data-id="element-144">
                                <button onClick={() => loStats.count > 0 && toggle(setExpandedLOs, lo.id)} className={`w-full flex items-center justify-between pl-[5.5rem] pr-6 py-2.5 transition-colors text-left border-t border-gray-50 ${loStats.count > 0 ? 'hover:bg-gray-50/80 cursor-pointer' : 'cursor-default'}`} data-id="element-145">
                                  <div className="flex items-center gap-2.5 min-w-0" data-id="element-146">
                                    {loStats.count > 0 ? <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform duration-200 flex-shrink-0 ${loOpen ? '' : '-rotate-90'}`} data-id="element-147" /> : <span className="w-3 flex-shrink-0" data-id="element-148" />}
                                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-149">
                                      <UserIcon className="w-3 h-3 text-gray-400" data-id="element-150" />
                                    </div>
                                    <span className="text-sm text-gray-700" data-id="element-151">
                                      {lo.name}
                                    </span>
                                  </div>
                                  <RowStats count={loStats.count} volume={loStats.volume} mode={viewMode} data-id="element-152" />
                                </button>

                                {/* Borrower Detail Rows */}
                                {loOpen && loStats.borrowers.length > 0 && <div className="bg-gray-50/60 border-t border-gray-100 overflow-x-auto" data-id="element-153">
                                    <table className="w-full text-sm" data-id="element-154">
                                      <thead data-id="element-155">
                                        <tr className="border-b border-gray-200/80" data-id="element-156">
                                          <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider pl-[7rem] pr-3 py-2" data-id="element-157">
                                            Borrower
                                          </th>
                                          <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2" data-id="element-158">
                                            Phone
                                          </th>
                                          <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2" data-id="element-159">
                                            Email
                                          </th>
                                          <th className="text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2" data-id="element-160">
                                            {isEligible ? 'Loan Amt' : 'Recap Amt'}
                                          </th>
                                          <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2" data-id="element-161">
                                            Type
                                          </th>
                                          <th className="text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2" data-id="element-162">
                                            Rate
                                          </th>
                                          <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2" data-id="element-163">
                                            {isEligible ? 'Funded' : 'Recaptured'}
                                          </th>
                                          <th className="text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider pl-3 pr-6 py-2" data-id="element-164">
                                            Actions
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody data-id="element-165">
                                        {loStats.borrowers.map(borrower => <tr key={borrower.id} className="border-b border-gray-100/80 last:border-b-0 hover:bg-gray-100/40 transition-colors" data-id="element-166">
                                            <td className="pl-[7rem] pr-3 py-2.5" data-id="element-167">
                                              <span className="font-medium text-gray-900" data-id="element-168">
                                                {borrower.name}
                                              </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap" data-id="element-169">
                                              {borrower.phone}
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap" data-id="element-170">
                                              {borrower.email}
                                            </td>
                                            <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${isEligible ? 'text-gray-900' : 'text-green-700'}`} data-id="element-171">
                                              {isEligible ? formatUSD(borrower.loanAmount) : formatUSD(borrower.recapturedAmount ?? borrower.loanAmount)}
                                            </td>
                                            <td className="px-3 py-2.5" data-id="element-172">
                                              <Badge variant="blue" data-id="element-173">
                                                {borrower.loanType}
                                              </Badge>
                                            </td>
                                            <td className="px-3 py-2.5 text-right tabular-nums" data-id="element-174">
                                              <span className={`font-medium ${borrower.rate > 7 ? 'text-amber-700' : 'text-gray-700'}`} data-id="element-175">
                                                {borrower.rate.toFixed(3)}%
                                              </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap tabular-nums" data-id="element-176">
                                              {isEligible ? new Date(borrower.fundedDate).toLocaleDateString() : borrower.recapturedDate ? new Date(borrower.recapturedDate).toLocaleDateString() : '—'}
                                            </td>
                                            <td className="pl-3 pr-6 py-2.5 text-right whitespace-nowrap" data-id="element-177">
                                              <div className="inline-flex items-center gap-1.5" data-id="element-178">
                                                {isEligible && <Button size="sm" variant="primary" onClick={e => {
                                e.stopPropagation();
                                setScriptBorrowerId(borrower.id);
                              }} data-id="element-179">
                                                    <FileTextIcon className="mr-1 h-3.5 w-3.5" data-id="element-180" />
                                                    Script
                                                  </Button>}
                                                <Button size="sm" variant="outline" data-id="element-181">
                                                  <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-182" />
                                                </Button>
                                              </div>
                                            </td>
                                          </tr>)}
                                      </tbody>
                                    </table>
                                  </div>}
                              </div>;
              })}
                      </div>;
          })}
              </div>;
      })}
      </CardContent>

      {/* ── Call Script Modal ── */}
      {scriptBorrower && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setScriptBorrowerId(null)} data-id="element-183">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-184">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white" data-id="element-185">
              <div data-id="element-186">
                <div className="flex items-center gap-2" data-id="element-187">
                  <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-188" />
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-189">
                    Call Script
                  </h2>
                </div>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-190">
                  For{' '}
                  <span className="font-medium text-blue-600" data-id="element-191">
                    {scriptBorrower.name}
                  </span>{' '}
                  · {scriptBorrower.phone}
                </p>
              </div>
              <button onClick={() => setScriptBorrowerId(null)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-192">
                <XIcon className="w-5 h-5 text-gray-400" data-id="element-193" />
              </button>
            </div>
            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-194">
              <div className="space-y-5" data-id="element-195">
                {callScript.map((block, idx) => <div key={idx} data-id="element-196">
                    <div className="flex items-center gap-2 mb-2" data-id="element-197">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-198">
                        {idx + 1}
                      </span>
                      <h3 className="text-xs font-semibold text-blue-600 uppercase tracking-wider" data-id="element-199">
                        {block.section}
                      </h3>
                    </div>
                    <div className="ml-8 bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-200">
                      <p className="text-sm text-gray-700 leading-relaxed" data-id="element-201">
                        "{block.text}"
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Footer */}
            <div className="px-6 py-3.5 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-202">
              <p className="text-xs text-gray-400" data-id="element-203">
                Generated from loan data · Customize as needed
              </p>
              <div className="flex gap-2" data-id="element-204">
                <Button variant="outline" size="sm" onClick={() => setScriptBorrowerId(null)} data-id="element-205">
                  Close
                </Button>
                <Button variant="primary" size="sm" data-id="element-206">
                  <PhoneIcon className="mr-1.5 h-3.5 w-3.5" data-id="element-207" />
                  Call {scriptBorrower.name.split(' ')[0]}
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </Card>;
}